import { jsx, jsxs } from "react/jsx-runtime";
import { string, bool, func, oneOfType, arrayOf, node, element, oneOf, checkPropTypes } from "prop-types";
import React, { forwardRef } from "react";
import SSRIcon from "@ingka/ssr-icon";
import cx from "classnames";
import warningTriangle from "@ingka/ssr-icon/paths/warning-triangle";
const inverseTypes = ["primary", "emphasised", "secondary", "tertiary"];
const propTypes = {
  /** Adds children after `text` element. */
  children: oneOfType([
    /** Array of React elements */
    arrayOf(node),
    /** React element */
    node
  ]),
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Button disabled state */
  disabled: bool,
  /** Makes text expand on hover if `iconOnly === true` */
  expanding: bool,
  /** Sets the width to 100% */
  fluid: bool,
  /** Will cause the button to render as an anchor tag linked to the provided URL */
  href: string,
  /** Button default type attributes (button, submit, reset) */
  htmlType: string,
  /** Ensures no text is rendered, adds `icon-` before the \${type} in `.btn--${type}` */
  iconOnly: bool,
  /** Icon position in the button (leading, trailing) */
  iconPosition: oneOf(["leading", "trailing"]),
  /** Sets the inverse theme to the button types accordingly. One of ['primary', 'emphasised', 'secondary', 'tertiary'] */
  inverseTheme: (props, propName, componentName) => {
    if (inverseTypes.indexOf(props.type) === -1 && props.inverseTheme) {
      return new Error(`The property: 'inverseTheme' provided to Button is not applicable to the Button of the type '${props.type}'. Please remove it or set it to false`);
    }
    return checkPropTypes({
      inverseTheme: bool
    }, props, "prop", componentName);
  },
  /** Set to loading button */
  loading: bool,
  /** Button text loading for screen readers */
  loadingSrText: oneOfType([
    /** String value */
    string,
    /** React element that can be parsed as string */
    element
  ]),
  /** Link opens in new window */
  newWindow: bool,
  /** Function that handles Button click */
  onClick: func,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Makes button height 40px instead of 56px */
  small: bool,
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  ssrIcon: func,
  /** Button text */
  text: oneOfType([
    /** String value */
    string,
    /** React element that can be parsed as string */
    element
  ]),
  /** Button type. One of ['primary', 'emphasised', 'secondary', 'tertiary', 'plain', 'danger', 'image-overlay'] */
  type: oneOf(["primary", "emphasised", "secondary", "tertiary", "danger", "plain", "image-overlay"]),
  /** Makes only the icon button extra small */
  xsmallIconOnly: bool,
  /** If `expanding === true` enforces that button is expanded always on touch devices */
  expandedOnTouchDevice: bool
};
const Button = forwardRef((props, ref) => {
  const {
    small,
    xsmallIconOnly,
    href,
    newWindow,
    loadingSrText = "Loading\u2026",
    ssrIcon,
    iconOnly = false,
    prefix = "",
    htmlType = "button",
    disabled = false,
    className,
    onClick,
    text,
    type = "secondary",
    expanding,
    loading,
    iconPosition = "leading",
    fluid,
    inverseTheme,
    children,
    expandedOnTouchDevice,
    ...others
  } = props;
  function onKeyUpHandler(e) {
    if (disabled || loading || e.key !== " ") {
      return true;
    }
    e.target.click();
  }
  let btnType = type ? type : "secondary";
  if (iconOnly && ssrIcon) {
    btnType = `icon-${btnType}`;
  }
  if (inverseTheme && inverseTypes.indexOf(type) > -1) {
    btnType = `${btnType}-inverse`;
  }
  const innerClasses = cx(
    `${prefix}btn__inner`
  );
  let iconTag;
  const iconClass = `${prefix}btn__icon`;
  const icon = type === "danger" ? warningTriangle : ssrIcon;
  if (icon) {
    iconTag = /* @__PURE__ */ jsx(SSRIcon, { className: iconClass, prefix, paths: icon });
  }
  const classes = cx(
    `${prefix}btn`,
    loading && `${prefix}btn--loading`,
    small && `${prefix}btn--small`,
    iconOnly && xsmallIconOnly && `${prefix}btn--xsmall`,
    !iconOnly && icon && iconPosition && `${prefix}${iconPosition}-icon`,
    type && `${prefix}btn--${btnType}`,
    fluid && !iconOnly && `${prefix}btn--fluid`,
    expanding && `${prefix}btn--expanding`,
    expanding && expandedOnTouchDevice && `${prefix}btn--touch-device-expanded`,
    className
  );
  const handleClick = (ev) => {
    if (loading || disabled) {
      ev.stopPropagation();
      return;
    }
    if (onClick && typeof onClick === "function")
      onClick(ev);
  };
  const ButtonTag = href ? "a" : "button";
  const onKeyUp = ButtonTag === "a" ? onKeyUpHandler : null;
  const role = href ? "button" : null;
  const linkProps = {
    ...!disabled && { href },
    ...newWindow && { target: "_blank" }
  };
  const button = /* @__PURE__ */ jsx(
    ButtonTag,
    {
      ref,
      "aria-disabled": loading,
      type: !href ? htmlType : null,
      role,
      disabled: href ? null : disabled,
      className: classes,
      onClick: handleClick,
      onKeyUp,
      ...linkProps,
      ...others,
      children: /* @__PURE__ */ jsxs("span", { className: innerClasses, children: [
        iconTag,
        text && /* @__PURE__ */ jsx("span", { className: `${prefix}btn__label`, children: text }),
        !loading && children,
        loading && /* @__PURE__ */ jsx("span", { className: `${prefix}btn__loader`, children: loadingSrText })
      ] })
    }
  );
  return button;
});
Button.propTypes = propTypes;
Button.displayName = "Button";
var Button_default = Button;
export {
  Button_default as default
};
